// Single source of truth for the mapping-detail (completeness) palette.
//
// Every surface that renders the three buckets reads from here — playground
// polygons (vectorStyles.js), cluster ring segments (clusterStyle.js), hub
// macro ring segments (macroRingStyle.js) and the legend
// (CompletenessLegend.svelte). Before this module the same colours were
// written out in all four places with a comment asking the reader to keep
// them in sync by hand.
//
// The scale is a single-hue green ramp ending in a cool slate — not a traffic
// light. A diverging red/amber/green scale encodes "good vs bad about a
// midpoint", which reads as a verdict on the playground. This one encodes
// "more vs less", which is what the value actually measures: how much of this
// playground has been mapped.
//
// Ordering is by LIGHTNESS, and monotonic: `complete` is the brightest step
// (#4ade80, L* 79), `partial` a mid green (#15803d, L* 47), `missing` a cool
// slate (#475569, L* 36). So the map draws the eye to well-mapped playgrounds,
// and a viewer with deuteranopia or protanopia can recover the ordering from
// lightness alone, without relying on the green/slate hue split.
//
// That ordering only works because of the casing. `base` is drawn OPAQUE on
// cluster and macro ring arcs, and against the OpenFreeMap Bright style the
// app ships (desaturated, every landcover surface between L* 87 and L* 96)
// #4ade80 measures 1.59:1 on the basemap background, 1.35:1 over grass and
// 1.06:1 over water — against a 3:1 floor for a graphical object. On a
// near-light ground "brighter" and ">= 3:1" pull in opposite directions, and a
// search of the green space returns nothing above 3:1 lighter than L* 31.
//
// The first attempt moved the whole ramp down a step instead: `complete` took
// green-700, `partial` dropped to a near-black #052e16. It cleared the floor
// and cost more than it bought. The bright end was gone, so the top bucket no
// longer pulled the eye; the ramp stopped being monotonic; and on the polygon
// tier, where the fills are translucent over a near-white basemap, `partial`
// and `missing` collapsed to dE 9.7 — the two lower buckets became hard to
// tell apart, which is the distinction the contribution prompt hangs on.
//
// Drawing a dark casing under each arc (RING_CASING, below) moves the contrast
// requirement off the fill and onto the edge, which is what road casings do in
// cartography. The ramp is then free to be chosen for meaning rather than for
// a ratio. Same polygon pair with the ramp restored: dE 9.7 -> 20.2.
//
// (An earlier version of this note cited 1.74:1 against "the white separator
// stroke the ring renderer draws between arcs". No such stroke exists:
// neither drawStackedRing nor renderHealthyMacroRing draws one, and the only
// white stroke in either renderer is on the single-child dot. The basemap
// measurements above are the whole case.)
//
// COST, recorded rather than solved:
//   The rings read heavier than they did. `missing` is the dominant bucket —
//   625 of 926 playgrounds in Fulda — and slate-600 sitting directly against a
//   dark casing makes a mostly-unmapped cluster read as a near-black disc at
//   low zoom. Reviewed on screen and kept for now; the levers if it needs
//   fixing are a lighter casing, a 1px casing instead of 2px, or lightening
//   `missing` (which the casing has made possible again, since `missing` only
//   went to slate-600 to clear the floor on its own).
//
//   Note the tension that sits under that last option: monotonic lightness
//   wants `missing` darkest, while "the least-mapped state should be quiet"
//   wants it lightest. Those are incompatible, and this palette currently
//   chooses the ordering.
//
// The zero case is a cool slate blue-grey, not a plain grey. A neutral colour
// is right — it reads as "nothing here yet" rather than "bad playground",
// which is the state we want to invite contributions for — but plain grey at
// low alpha disappeared into the basemap, which renders residential landuse
// (#e0dfdf) and buildings (#d9d0c9) in warm greys of its own. The slight blue
// cast separates it from those without making it look like a judgement.
//
// It sits at slate-600 (#475569), not slate-500. Slate-500 measured 2.91:1
// over water — under the same 3:1 floor for a graphical object that moved the
// greens down a step, so it failed the rule the rest of the ramp was rebuilt
// to satisfy. Slate-600 is 4.63:1 worst case. The move also pulls `missing`
// (L* 36) clear of `complete` (L* 47), which had collided at L* 48 and left
// the two indistinguishable by lightness.
//
// This bucket covers most of the map — 625 of 926 playgrounds in Fulda — so it
// has to stay quiet while remaining findable. Its fill sits at 0.24 with a
// dark stroke: the outline carries "there is a playground here", the fill
// stays out of the way.
//
// Bucket keys stay `complete` / `partial` / `missing` — those are the wire
// and storage identifiers (see get_playground_clusters, get_meta,
// playground_stats). Their user-facing labels are "detailed" / "basic" /
// "no details yet" and live in locales/*.json under `mappingDetail.*`.

/**
 * Base colours, one per bucket.
 *
 * `base`   — the bucket's identity colour, drawn opaque.
 * `fill`   — the base at low alpha, for shapes laid over the basemap.
 * `stroke` — outline / border.
 * `hatch`  — access-restricted polygons: diagonal hatch stroke over a faint
 *            background wash, both derived from the base.
 *
 * WHICH FIELD GOES WHERE — get this wrong and two surfaces showing the same
 * bucket render in visibly different colours:
 *
 *   surface                        field    why
 *   ─────────────────────────────  ───────  ──────────────────────────────────
 *   cluster ring segments          base     opaque arcs on the map
 *   macro ring segments            base     same
 *   legend swatches                base     must equal the rings beside them
 *   hub drawer dots + bar          base     opaque
 *   playground polygons            fill     translucent over the basemap
 *   panel badge background         fill     dark text must stay readable —
 *                                           the badge's dot uses `base`
 *
 * Rule of thumb: anything opaque uses `base`; only shapes the basemap shows
 * through, or backgrounds carrying text, use `fill`.
 */
/**
 * Per-bucket identity colour. Everything else is derived from it, so `base`
 * and the polygon fill cannot drift apart again.
 *
 * They had. When the ramp moved down a step to clear the contrast floor, only
 * `base` moved: `complete` rings became green-700 while `complete` polygons
 * stayed mint, so one playground changed colour crossing clusterMaxZoom. Worse,
 * `partial`'s hand-written fill was built from #15803d — the hex that is now
 * `complete`'s ring — so the same colour meant "basic" as a polygon and
 * "detailed" as an arc.
 */
const BASE = {
    complete: '#4ade80',
    partial:  '#15803d',
    missing:  '#475569',
};

/**
 * Casing drawn under each ring arc, one pixel proud on each side.
 *
 * This is what meets the 3:1 floor for the ring, so `base` does not have to.
 * Without it the brightest step in the ramp was the least visible thing on the
 * map: opaque #4ade80 measures 1.06:1 over water. A dark casing gives every
 * arc a hard edge against the basemap regardless of its fill, which is how
 * road casings work in cartography, and frees the ramp to be ordered by
 * lightness instead of by what happens to clear a contrast ratio.
 */
export const RING_CASING = '#1f2937';

/** Outline colour. Not derived: it is chosen for contrast against the fill. */
const STROKE = {
    complete: '#15803d',
    partial:  '#14532d',
    missing:  '#334155',
};

function withAlpha(hex, alpha) {
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

// One alpha for all three buckets, not three hand-tuned ones.
//
// Measured over the basemap background (#f8f4f0), pairwise CIE dE between the
// three composited fills:
//
//   today, hand-written fills          complete-partial 10.6   partial-missing 12.7
//   derived, today's mixed alphas      complete-partial 11.5   partial-missing  8.0
//   derived, uniform 0.30              complete-partial 12.0   partial-missing 11.8
//   derived, uniform 0.38              complete-partial 15.5   partial-missing 15.5
//
// Mixed alphas would have regressed partial-vs-missing to 8.0. 0.38 separates
// best but puts real weight on a layer that is 67% `missing` in Fulda and may
// yet become opt-in, so 0.30 takes parity with today at the lower weight.
//
// Note these are all far below the ring separation (complete-vs-partial is
// dE 42 as opaque arcs). Translucent fills over a near-white ground wash
// toward each other; the polygon tier is inherently a weaker signal than the
// legend, which shows `base`, implies.
const FILL_ALPHA  = 0.34;
const HATCH_LINE  = 0.55;
const HATCH_WASH  = 0.08;

export const COMPLETENESS_PALETTE = Object.fromEntries(
    Object.entries(BASE).map(([key, base]) => [key, {
        base,
        fill:   withAlpha(base, FILL_ALPHA),
        stroke: STROKE[key],
        hatch:  { stroke: withAlpha(base, HATCH_LINE), bg: withAlpha(base, HATCH_WASH) },
    }])
);

/** Bucket keys in ramp order (most mapped → least). Drives legend order. */
export const COMPLETENESS_ORDER = ['complete', 'partial', 'missing'];

/** Base colour per bucket — the shape clusterStyle/macroRingStyle want. */
export const COMPLETENESS_BASE = {
    complete: COMPLETENESS_PALETTE.complete.base,
    partial:  COMPLETENESS_PALETTE.partial.base,
    missing:  COMPLETENESS_PALETTE.missing.base,
};

// ── Finder palette (mapping detail off, #924) ────────────────────────────────
//
// With mapping detail off every playground marker — cluster disc, pin, macro
// disc, selected outline — is one emerald. Chosen over a warm orange after a
// side-by-side live review (2026-10-12): it matches the app's own green
// controls and reads calmer at the cluster tier. Known costs, accepted:
//
//   - It shares a hue family with the mapping-detail greens. That is only a
//     problem across modes — the two are never on the map together — and the
//     finder view shows no legend that could suggest a meaning.
//   - Inside a selected playground it sits among green tree dots, grass and
//     pitch polygons, so the outline separates less than orange did.
//
// WCAG contrast of the fill alone, measured against the basemap:
//
//                 ground   landuse  buildings  water    park
//                 #f8f4f0  #e0dfdf  #d9d0c9    #a0c8f0  #d8e8c8
//   #059669 fill    3.44     2.83     2.48       2.16     2.92
//   #065f46 casing  7.02     5.78     5.06       4.40     5.96
//
// The fill clears 3:1 only on open ground, so — as with RING_CASING — a dark
// casing carries the edge and the fill is free to be the friendly colour.
// White count text on the fill is 3.77:1, short of 4.5:1 for 16px bold, so the
// count gets a casing-coloured halo (white on #065f46 is 7.68:1).
export const FINDER = {
    fill:         '#059669', // emerald-600
    casing:       '#065f46', // emerald-800
    border:       '#ffffff',
    text:         '#ffffff',
    selectedFill: 'rgba(5, 150, 105, 0.12)',
};
